import { Component } from '@theme/component';

/**
 * UpCart-style cart reservation countdown for urgency.
 *
 * Shows "reserved for MM:SS". The end time is persisted in localStorage so the
 * countdown stays continuous across drawer re-opens and page loads. When it
 * reaches zero it loops back to a fresh duration so the urgency never disappears.
 *
 * @extends {Component}
 */
class CartTimerComponent extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  static #STORAGE_KEY = 'cart-timer-end';

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#interval);
  }

  /** @returns {number} duration in ms */
  #duration() {
    return (Number(this.dataset.minutes) || 10) * 60 * 1000;
  }

  /** @returns {number} end timestamp (ms), (re)starting if missing/expired */
  #endTime() {
    let end = Number(localStorage.getItem(CartTimerComponent.#STORAGE_KEY));
    const now = Date.now();
    if (!end || end <= now) {
      end = now + this.#duration();
      try {
        localStorage.setItem(CartTimerComponent.#STORAGE_KEY, String(end));
      } catch (e) {
        /* private mode / storage disabled — timer still runs for this view */
      }
    }
    return end;
  }

  #tick() {
    const remaining = Math.max(0, this.#endTime() - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');

    const el = this.refs.time ?? this.querySelector('.cart-timer__time');
    if (el) el.textContent = `${minutes}:${seconds}`;
  }
}

if (!customElements.get('cart-timer-component')) {
  customElements.define('cart-timer-component', CartTimerComponent);
}
